import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Alert } from '../components/Alert'

const trimCanvasBottom = (canvas, padding = 8) => {
  const { width, height } = canvas
  const ctx = canvas.getContext('2d')
  if (!ctx || width === 0 || height === 0) return canvas

  const { data } = ctx.getImageData(0, 0, width, height)
  const isWhite = (r, g, b, a) =>
    a < 10 || (r > 250 && g > 250 && b > 250)

  let lastRow = -1
  for (let y = height - 1; y >= 0; y -= 1) {
    const rowStart = y * width * 4
    for (let x = 0; x < width; x += 1) {
      const idx = rowStart + x * 4
      if (!isWhite(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])) {
        lastRow = y
        y = -1
        break
      }
    }
  }

  if (lastRow === -1) return canvas

  const trimmedHeight = Math.min(height, lastRow + 1 + padding)
  if (trimmedHeight >= height) return canvas

  const trimmedCanvas = document.createElement('canvas')
  trimmedCanvas.width = width
  trimmedCanvas.height = trimmedHeight
  trimmedCanvas
    .getContext('2d')
    .drawImage(canvas, 0, 0, width, trimmedHeight, 0, 0, width, trimmedHeight)

  return trimmedCanvas
}

const applyPdfExpansionsInClone = doc => {
  if (!doc) return
  const elements = Array.from(doc.querySelectorAll('[data-pdf-expand]'))
  if (!elements.length) return
  const view = doc.defaultView
  if (!view) return

  const hasOverflow = element => {
    if (!element) return false
    const style = view.getComputedStyle(element)
    const canScrollY =
      style.overflowY === 'auto' || style.overflowY === 'scroll'
    const canScrollX =
      style.overflowX === 'auto' || style.overflowX === 'scroll'
    const overY = element.scrollHeight > element.clientHeight + 1
    const overX = element.scrollWidth > element.clientWidth + 1
    return (canScrollY && overY) || (canScrollX && overX)
  }

  const needsExpand = new Set()

  elements.forEach(element => {
    const mode = element.getAttribute('data-pdf-expand') || 'height'
    if (mode === 'scroll') {
      needsExpand.add(element)
      return
    }

    if (hasOverflow(element)) {
      needsExpand.add(element)
    }
  })

  elements.forEach(element => {
    const mode = element.getAttribute('data-pdf-expand') || 'height'
    if (mode !== 'height' || needsExpand.has(element)) return
    const scrollDescendants = element.querySelectorAll(
      '[data-pdf-expand="scroll"]',
    )
    for (const descendant of scrollDescendants) {
      if (hasOverflow(descendant)) {
        needsExpand.add(element)
        break
      }
    }
  })

  needsExpand.forEach(element => {
    const mode = element.getAttribute('data-pdf-expand') || 'height'
    if (mode === 'scroll') {
      const fullHeight = element.scrollHeight
      if (fullHeight) {
        element.style.height = `${fullHeight}px`
      } else {
        element.style.height = 'auto'
      }
      element.scrollTop = 0
      element.scrollLeft = 0
    } else {
      element.style.height = 'auto'
    }
    element.style.maxHeight = 'none'
    element.style.overflow = 'visible'
    element.style.overflowY = 'visible'
    element.style.overflowX = 'visible'
    if (
      mode === 'scroll' &&
      element.getAttribute('data-pdf-keep-flex') !== 'true'
    ) {
      element.style.flex = 'none'
    }
  })
}

const applyPdfToggleFix = doc => {
  if (!doc) return
  const tracks = doc.querySelectorAll('[data-pdf-toggle="track"]')
  if (!tracks.length) return
  const inactiveTrackColor = 'rgb(230, 234, 240)'
  const view = doc.defaultView

  tracks.forEach(track => {
    if (!view) return
    const trackStyle = view.getComputedStyle(track)
    if (trackStyle.backgroundColor !== inactiveTrackColor) return
    const thumb = track.querySelector('[data-pdf-toggle="thumb"]')
    if (!thumb) return
    const trackHeight = track.offsetHeight
    const thumbHeight = thumb.offsetHeight
    if (!trackHeight || !thumbHeight) return
    const top = Math.max(
      0,
      Math.round((trackHeight - thumbHeight) / 2) - 1,
    )
    thumb.style.top = `${top}px`
    thumb.style.boxShadow = 'none'
  })
}

const applyPdfHiddenElements = doc => {
  if (!doc) return
  const elements = doc.querySelectorAll('[data-pdf-hide]')
  if (!elements.length) return
  elements.forEach(element => {
    element.style.display = 'none'
  })
}

const applyPdfTypewriterFix = doc => {
  if (!doc) return
  const textElements = doc.querySelectorAll('[data-pdf-full-text]')
  if (textElements.length) {
    textElements.forEach(element => {
      const fullText = element.getAttribute('data-pdf-full-text')
      if (fullText !== null) {
        element.textContent = fullText
      }
    })
  }
  const cursors = doc.querySelectorAll('.cursor')
  if (cursors.length) {
    cursors.forEach(cursor => {
      cursor.style.display = 'none'
    })
  }
}

const applyPdfRiskStoryFix = doc => {
  if (!doc) return
  const view = doc.defaultView

  const setSafeLineHeight = element => {
    if (!element) return
    const multiplier = 1.75
    if (view) {
      const computed = view.getComputedStyle(element)
      const fontSize = parseFloat(computed.fontSize || '')
      if (Number.isFinite(fontSize) && fontSize > 0) {
        const nextLineHeight = Math.ceil(fontSize * multiplier)
        element.style.lineHeight = `${nextLineHeight}px`
      } else {
        element.style.lineHeight = `${multiplier}`
      }
    } else {
      element.style.lineHeight = `${multiplier}`
    }
    element.style.wordBreak = 'break-word'
    element.style.overflowWrap = 'break-word'
  }

  const riskTitles = doc.querySelectorAll('[data-pdf-risk-title]')
  if (riskTitles.length) {
    riskTitles.forEach(title => setSafeLineHeight(title))
  }

  const inlineDates = doc.querySelectorAll('[data-pdf-inline-date]')
  if (inlineDates.length) {
    inlineDates.forEach(dateSpan => {
      dateSpan.style.display = 'inline-block'
      dateSpan.style.marginRight = '4px'
      dateSpan.style.verticalAlign = 'baseline'
      const title = dateSpan.closest('[data-pdf-risk-title]')
      if (title) {
        setSafeLineHeight(title)
      }
    })
  }
}

const applyPdfScrollbarFix = doc => {
  if (!doc) return
  const style = doc.createElement('style')
  style.setAttribute('data-pdf-style', 'true')
  style.textContent = `
    [data-pdf-expand] { scrollbar-width: none !important; -ms-overflow-style: none !important; }
    [data-pdf-expand]::-webkit-scrollbar { width: 0 !important; height: 0 !important; }
    [data-scroll-indicator="true"] { background-image: none !important; }
    [data-pdf-risk-title] { line-height: 1.75 !important; word-break: break-word !important; overflow-wrap: break-word !important; }
    [data-pdf-risk-title] * { line-height: inherit !important; }
  `
  doc.head && doc.head.appendChild(style)
}

export const makePDF = async (documentId, fileName, ignoreElementItems) => {
  const input = document.getElementById(documentId)
  const scrollX =
    window.pageXOffset || document.documentElement.scrollLeft || 0
  const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0

  await html2canvas(input, {
    logging: false,
    scrollX: -scrollX,
    scrollY: -scrollY,
    ignoreElements: element => ignoreElementItems.includes(element.id),
    onclone: clonedDocument => {
      applyPdfTypewriterFix(clonedDocument)
      applyPdfRiskStoryFix(clonedDocument)
      applyPdfScrollbarFix(clonedDocument)
      applyPdfHiddenElements(clonedDocument)
      applyPdfToggleFix(clonedDocument)
      applyPdfExpansionsInClone(clonedDocument)
    },
  })
    .then(canvas => {
      const trimmedCanvas = trimCanvasBottom(canvas)
      const orientation =
        trimmedCanvas.width > trimmedCanvas.height ? 'landscape' : 'portrait'
      const imgData = trimmedCanvas.toDataURL('image/png')
      const doc = new jsPDF(orientation, 'px', [
        trimmedCanvas.width,
        trimmedCanvas.height,
      ])
      doc.addImage(
        imgData,
        'JPEG',
        0,
        0,
        trimmedCanvas.width,
        trimmedCanvas.height,
        undefined,
        'FAST',
      )
      doc.save(`${fileName}.pdf`)
    })
    .catch(e => {
      Alert.error('Export to PDF unsuccessfully')
    })
}
